/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfirmation, useRequester } from '../../hooks';
import ScenarioResource, { ScenarioData } from '../../resources/ScenarioResource';
import { useEffect } from 'react';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Page } from '../../design';

export default function ScenarioPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const deleteConfirmation = useConfirmation<ScenarioData>({
    getTitle: (scenario: ScenarioData) => `Delete scenario ${scenario.title}?`,
    getDescription: (scenario: ScenarioData) =>
      `You are about to delete ${scenario.title}. This action cannot be undone.`,
    onConfirm: async (scenario: ScenarioData) => {
      await ScenarioResource.deleteOne(scenario.id);
      navigate('/scenarios');
    },
  });

  const fetchScenario = useRequester<ScenarioResource>({
    onSubmit: (id) => ScenarioResource.fetchOne(id),
    onError: console.log,
  });

  useEffect(() => {
    fetchScenario.submit(id);
  }, [id]);

  if (!id) return <Alert severity="error">No scenario ID provided</Alert>;

  const scenario = ScenarioResource.get(id).data as ScenarioData;
  if (!scenario) {
    return (
      <Box className="flex flex-col items-center h-full justify-center">
        {fetchScenario.error ? (
          <Alert severity="error">{fetchScenario.error}</Alert>
        ) : (
          <CircularProgress />
        )}
      </Box>
    );
  }

  return (
    <Page>
      <Page.Header>
        <Box className="flex flex-col h-full justify-center">
          <Box className="flex flex-row gap-4">
            <Box className="flex flex-col gap-1 flex-grow">
              <Box className="flex flex-row gap-1 items-center">
                <Typography variant="h1" sx={{ mr: 1 }}>
                  {scenario.title}
                </Typography>
                {fetchScenario.loading && (
                  <CircularProgress size={20} sx={{ ml: 1 }} />
                )}
                <Box className="flex-grow"></Box>
                <IconButton color="primary" onClick={() => navigate(`/scenarios/${id}/edit`)}>
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="error"
                  sx={{ ml: -1 }}
                  onClick={() => deleteConfirmation.triggerFor(scenario)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <Typography variant="body1" color="text.secondary" textAlign="justify">
                {scenario.description}
              </Typography>

              {scenario.audioTrack && (
                <Box sx={{ mt: 2 }}>
                  <audio controls>
                    <source src={scenario.audioTrack} />
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Page.Header>
      <Page.Content>
        <Box className="flex flex-col gap-8 mt-6">
          {scenario.summary && (
            <Box className="flex flex-col gap-1">
              <Typography variant="h4" fontWeight="bold">
                Summary
              </Typography>
              <Typography textAlign="justify" sx={{ pl: 2, mt: 2 }}>
                {scenario.summary}
              </Typography>
            </Box>
          )}

          {scenario.lines && scenario.lines.length > 0 && (
            <Box className="flex flex-col gap-4">
              <Typography variant="h4" fontWeight="bold">
                Dialogue Lines
              </Typography>
              <Box className="flex flex-col gap-3 mt-2">
                {scenario.lines.map((line, index) => (
                  <Box key={index} className="flex flex-row gap-3 items-start">
                    <Avatar
                      src={line.participantAvatar}
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: 'primary.main',
                        fontSize: '0.75rem',
                        flexShrink: 0,
                      }}
                    >
                      {line.participantName
                        ? line.participantName.charAt(0).toUpperCase()
                        : line.participantId
                          ? line.participantId.charAt(0).toUpperCase()
                          : (index + 1).toString()}
                    </Avatar>
                    <Box className="flex flex-col flex-grow min-w-0">
                      {(line.participantName || line.participantRole) && (
                        <Box className="flex flex-row items-center gap-1.5 mb-1">
                          {line.participantName && (
                            <Typography
                              variant="subtitle2"
                              fontWeight="600"
                              sx={{ lineHeight: 1.2 }}
                            >
                              {line.participantName}
                            </Typography>
                          )}
                          {line.participantRole && (
                            <Chip
                              label={line.participantRole}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                bgcolor: 'action.selected',
                                color: 'text.secondary',
                              }}
                            />
                          )}
                        </Box>
                      )}
                      <Typography variant="body1" sx={{ mb: 0.5 }}>
                        {line.text}
                      </Typography>
                      {line.translation && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: 'italic', mb: 0.5 }}
                        >
                          {line.translation}
                        </Typography>
                      )}
                      {line.audio && (
                        <Box sx={{ mt: 0.5 }}>
                          <audio controls style={{ width: '100%', maxWidth: '400px' }}>
                            <source src={line.audio} />
                            Your browser does not support the audio element.
                          </audio>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {scenario.missions &&
            (scenario.missions.beginner?.length > 0 ||
              scenario.missions.intermediate?.length > 0 ||
              scenario.missions.advanced?.length > 0) && (
              <Box className="flex flex-col gap-1">
                <Typography variant="h4" fontWeight="bold">
                  Missions
                </Typography>
                <Box className="flex flex-col gap-6 mt-2" sx={{ pl: 2 }}>
                  {scenario.missions.beginner?.length > 0 && (
                    <Box className="flex flex-col gap-2">
                      <Typography variant="h6" fontWeight="600">
                        Beginner
                      </Typography>
                      {scenario.missions.beginner.map((mission: any, index: number) => (
                        <Box key={`beginner-${index}`} className="flex flex-col gap-1" sx={{ pl: 2 }}>
                          <Typography variant="body1" fontWeight="500">
                            {mission.phrase}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {mission.definition}
                          </Typography>
                          {mission.example && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Example: {mission.example}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {scenario.missions.intermediate?.length > 0 && (
                    <Box className="flex flex-col gap-2">
                      <Typography variant="h6" fontWeight="600">
                        Intermediate
                      </Typography>
                      {scenario.missions.intermediate.map((mission: any, index: number) => (
                        <Box key={`intermediate-${index}`} className="flex flex-col gap-1" sx={{ pl: 2 }}>
                          <Typography variant="body1" fontWeight="500">
                            {mission.phrase}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {mission.definition}
                          </Typography>
                          {mission.example && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Example: {mission.example}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {scenario.missions.advanced?.length > 0 && (
                    <Box className="flex flex-col gap-2">
                      <Typography variant="h6" fontWeight="600">
                        Advanced
                      </Typography>
                      {scenario.missions.advanced.map((mission: any, index: number) => (
                        <Box key={`advanced-${index}`} className="flex flex-col gap-1" sx={{ pl: 2 }}>
                          <Typography variant="body1" fontWeight="500">
                            {mission.phrase}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {mission.definition}
                          </Typography>
                          {mission.example && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              Example: {mission.example}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

          {scenario.dialogueAnalysis && (
            <Box className="flex flex-col gap-1">
              <Typography variant="h4" fontWeight="bold">
                Dialogue Analysis
              </Typography>
              <Box className="flex flex-col gap-4 mt-2" sx={{ pl: 2 }}>
                {/* Goals */}
                {scenario.dialogueAnalysis.goals?.length > 0 && (
                  <Box className="flex flex-col gap-2">
                    <Typography variant="h6" fontWeight="600">
                      Learning Goals
                    </Typography>
                    <Box className="flex flex-col gap-2">
                      {scenario.dialogueAnalysis.goals.map((goal: any, index: number) => (
                        <Paper key={`goal-${index}`} elevation={0} sx={{ p: 2, bgcolor: 'action.hover' }}>
                          <Box className="flex flex-col gap-1">
                            {goal.title?.en && (
                              <Typography variant="body1" fontWeight="500">
                                {goal.title.en}
                              </Typography>
                            )}
                            {goal.title?.ja && (
                              <Typography variant="body2" color="text.secondary">
                                {goal.title.ja}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Key Phrases */}
                {scenario.dialogueAnalysis.keyPhrases?.length > 0 && (
                  <Box className="flex flex-col gap-2">
                    <Typography variant="h6" fontWeight="600">
                      Key Phrases
                    </Typography>
                    <Box className="flex flex-col gap-2">
                      {scenario.dialogueAnalysis.keyPhrases.map((phrase: any, index: number) => (
                        <Paper key={`phrase-${index}`} elevation={0} sx={{ p: 2, bgcolor: 'action.hover' }}>
                          <Box className="flex flex-col gap-1">
                            {phrase.title?.en && (
                              <Typography variant="body1" fontWeight="500">
                                {phrase.title.en}
                              </Typography>
                            )}
                            {phrase.title?.ja && (
                              <Typography variant="body2" color="text.secondary">
                                {phrase.title.ja}
                              </Typography>
                            )}
                            {phrase.description?.ja && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {phrase.description.ja}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Grammar */}
                {scenario.dialogueAnalysis.grammar?.length > 0 && (
                  <Box className="flex flex-col gap-2">
                    <Typography variant="h6" fontWeight="600">
                      Grammar Points
                    </Typography>
                    <Box className="flex flex-col gap-2">
                      {scenario.dialogueAnalysis.grammar.map((grammar: any, index: number) => (
                        <Paper key={`grammar-${index}`} elevation={0} sx={{ p: 2, bgcolor: 'action.hover' }}>
                          <Box className="flex flex-col gap-1">
                            {grammar.title?.en && (
                              <Typography variant="body1" fontWeight="500">
                                {grammar.title.en}
                              </Typography>
                            )}
                            {grammar.usageExample?.en && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Example: {grammar.usageExample.en}
                              </Typography>
                            )}
                            {grammar.description?.ja && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {grammar.description.ja}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Business Etiquette */}
                {scenario.dialogueAnalysis.businessEtiquette?.length > 0 && (
                  <Box className="flex flex-col gap-2">
                    <Typography variant="h6" fontWeight="600">
                      Business Etiquette
                    </Typography>
                    <Box className="flex flex-col gap-2">
                      {scenario.dialogueAnalysis.businessEtiquette.map((etiquette: any, index: number) => (
                        <Paper key={`etiquette-${index}`} elevation={0} sx={{ p: 2, bgcolor: 'action.hover' }}>
                          <Box className="flex flex-col gap-1">
                            {etiquette.title?.en && (
                              <Typography variant="body1" fontWeight="500">
                                {etiquette.title.en}
                              </Typography>
                            )}
                            {etiquette.title?.ja && (
                              <Typography variant="body2" color="text.secondary">
                                {etiquette.title.ja}
                              </Typography>
                            )}
                            {etiquette.mannerPoints?.en && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" fontWeight="500" color="text.secondary">
                                  Manner Points:
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                                  {etiquette.mannerPoints.en}
                                </Typography>
                                {etiquette.mannerPoints?.ja && (
                                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                                    {etiquette.mannerPoints.ja}
                                  </Typography>
                                )}
                              </Box>
                            )}
                            {etiquette.description?.ja && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {etiquette.description.ja}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Action Items */}
                {(scenario.dialogueAnalysis.actionItemsBeforeCall?.length > 0 ||
                  scenario.dialogueAnalysis.actionItemsDuringCall?.length > 0 ||
                  scenario.dialogueAnalysis.actionItemsAfterCall?.length > 0) && (
                    <Box className="flex flex-col gap-2">
                      <Typography variant="h6" fontWeight="600">
                        Action Items
                      </Typography>
                      <Box className="flex flex-col gap-4">
                        {scenario.dialogueAnalysis.actionItemsBeforeCall?.length > 0 && (
                          <Box className="flex flex-col gap-2">
                            <Typography variant="subtitle1" fontWeight="600" color="text.secondary">
                              Before Call
                            </Typography>
                            <Box className="flex flex-col gap-2" sx={{ pl: 2 }}>
                              {scenario.dialogueAnalysis.actionItemsBeforeCall.map((item: any, index: number) => (
                                <Paper key={`before-${index}`} elevation={0} sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                                  <Box className="flex flex-col gap-0.5">
                                    {item.title?.en && (
                                      <Typography variant="body2" fontWeight="500">
                                        {item.title.en}
                                      </Typography>
                                    )}
                                    {item.title?.ja && (
                                      <Typography variant="body2" color="text.secondary">
                                        {item.title.ja}
                                      </Typography>
                                    )}
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {scenario.dialogueAnalysis.actionItemsDuringCall?.length > 0 && (
                          <Box className="flex flex-col gap-2">
                            <Typography variant="subtitle1" fontWeight="600" color="text.secondary">
                              During Call
                            </Typography>
                            <Box className="flex flex-col gap-2" sx={{ pl: 2 }}>
                              {scenario.dialogueAnalysis.actionItemsDuringCall.map((item: any, index: number) => (
                                <Paper key={`during-${index}`} elevation={0} sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                                  <Box className="flex flex-col gap-0.5">
                                    {item.title?.en && (
                                      <Typography variant="body2" fontWeight="500">
                                        {item.title.en}
                                      </Typography>
                                    )}
                                    {item.title?.ja && (
                                      <Typography variant="body2" color="text.secondary">
                                        {item.title.ja}
                                      </Typography>
                                    )}
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        )}

                        {scenario.dialogueAnalysis.actionItemsAfterCall?.length > 0 && (
                          <Box className="flex flex-col gap-2">
                            <Typography variant="subtitle1" fontWeight="600" color="text.secondary">
                              After Call
                            </Typography>
                            <Box className="flex flex-col gap-2" sx={{ pl: 2 }}>
                              {scenario.dialogueAnalysis.actionItemsAfterCall.map((item: any, index: number) => (
                                <Paper key={`after-${index}`} elevation={0} sx={{ p: 1.5, bgcolor: 'action.hover' }}>
                                  <Box className="flex flex-col gap-0.5">
                                    {item.title?.en && (
                                      <Typography variant="body2" fontWeight="500">
                                        {item.title.en}
                                      </Typography>
                                    )}
                                    {item.title?.ja && (
                                      <Typography variant="body2" color="text.secondary">
                                        {item.title.ja}
                                      </Typography>
                                    )}
                                  </Box>
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>
          )}
        </Box>
      </Page.Content>
    </Page >
  );
}
